/* eslint-disable react/prop-types */
/**
 * ReasonInput — shared preset + free-text reason picker.
 *
 * Contract per Part 5:
 *   - Preset buttons carry a reason_code; single-tap selects.
 *   - A free-text area is ALWAYS visible alongside the presets.
 *   - Free text alone (no preset) → { reason_code: "OTHER", note }
 *   - Preset + note → { reason_code: <preset>, note }
 *   - Preset alone → { reason_code: <preset>, note: "" }
 *
 * The parent owns state; this is a pure controlled component. The
 * hook `useReasonState()` initialises the shape and returns a bag
 * suitable for passing straight into ReasonInput.
 */
import React, { useState } from "react";

export function useReasonState(initial = { reason_code: null, note: "" }) {
  const [value, setValue] = useState(initial);
  return [value, setValue];
}

/**
 * Derive the payload shape a dispatch expects. Never returns free
 * text without a code — falls back to OTHER when no preset was
 * chosen.
 */
export function normaliseReason(value) {
  const note = (value?.note || "").trim();
  if (value?.reason_code) return { reason_code: value.reason_code, note };
  if (note) return { reason_code: "OTHER", note };
  return { reason_code: null, note: "" };
}

/** True iff the reason is complete enough to submit. */
export function reasonValid(value) {
  const n = normaliseReason(value);
  return !!n.reason_code;
}

export default function ReasonInput({ presets, value, onChange, disabled, testidPrefix }) {
  const code = value?.reason_code || null;
  const note = value?.note || "";
  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-reason-block`}>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1.5">
          Reason · tap a preset or type your own
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid={`${testidPrefix}-reason-presets`}>
          {presets.map((p) => {
            const on = code === p.code;
            return (
              <button key={p.code} type="button" disabled={disabled}
                onClick={() => onChange({ reason_code: on ? null : p.code, note })}
                data-testid={`${testidPrefix}-reason-preset-${p.code}`}
                className={`min-h-[44px] px-3 rounded-full border text-[12px] font-medium transition
                  ${on
                    ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-white"
                    : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}
                  disabled:opacity-40 disabled:cursor-not-allowed`}>
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1.5">
          Additional note (optional if a preset is selected)
        </div>
        <textarea value={note}
          onChange={(e) => onChange({ reason_code: code, note: e.target.value })}
          disabled={disabled}
          rows={2}
          placeholder="Free text — with no preset, recorded as reason OTHER"
          className="w-full rounded border border-[color:var(--pos-color-border,#E2E8DF)] p-2 text-[13px] text-[color:var(--pos-color-primary-text,#1A2421)] focus:border-[color:var(--color-primary)] focus:outline-none disabled:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"
          data-testid={`${testidPrefix}-reason-note`} />
      </div>
      {!code && note.trim() && (
        <div className="text-[10px] italic text-[color:var(--pos-color-secondary-text,#5C6B64)]"
          data-testid={`${testidPrefix}-reason-other-hint`}>
          Recording as <strong>OTHER</strong> with your note.
        </div>
      )}
    </div>
  );
}

// ─── Preset banks per Part 5 ────────────────────────────────────────
export const UNAVAILABLE_PRESETS = [
  { code: "MERGED",      label: "Merged" },
  { code: "RESERVED",    label: "Reserved" },
  { code: "MAINTENANCE", label: "Maintenance" },
  { code: "CLEANING",    label: "Cleaning" },
];

export const VOID_PRESETS = [
  { code: "GUEST_ABANDONED",  label: "Guest abandoned" },
  { code: "PRICING_ERROR",    label: "Pricing error" },
  { code: "ITEM_UNAVAILABLE", label: "Item unavailable" },
  { code: "DUPLICATE_ORDER",  label: "Duplicate order" },
];

export const CANCEL_PRESETS = [
  { code: "GUEST_CHANGED_MIND",  label: "Guest changed mind" },
  { code: "WRONG_ORDER_TAKEN",   label: "Wrong order taken" },
  { code: "ITEM_UNAVAILABLE",    label: "Item unavailable" },
  { code: "KITCHEN_MISTAKE",     label: "Kitchen mistake" },
];

export const DISCOUNT_PRESETS = [
  { code: "LOYALTY",          label: "Loyalty" },
  { code: "MANAGER_DISCRETION", label: "Manager discretion" },
  { code: "STAFF_MEAL",       label: "Staff meal" },
  { code: "COMPLAINT",        label: "Complaint" },
  { code: "PROMOTION",        label: "Promotion" },
];
